import { purposeFamily } from "@/purpose-sections/catalogue";
import { resolveBackground } from "@/utils/background-layer";
import { ownBackgroundStyle, surfaceOf } from "@/utils/color-system";
import { ownColourCss, ownColourInDark } from "@/utils/colour-versions";
import { cornerLengths } from "@/utils/form-values";
import { isLookSystem } from "@/utils/look-gate";
import { resolvePaletteKey, rolePaletteKey } from "@/utils/palette-roles";
import {
  livingScrim,
  resolveLivingBackground,
  type LivingScrim,
} from "@/utils/living-background";

/** What a section wrapper adds to its <section> on a look-system website. */
export interface SectionLookAttributes {
  /** A palette key, or for an own colour its hex, which no palette key can be. */
  "data-re-bg"?: string;
  /** An own colour's derived colours; the dark ones as `--re-dark-*`, which colorSystemCss puts in place. */
  style?: Record<string, string>;
  /** Paints an own colour as chosen in light, where its derived fill may differ, and its dark fill in dark. */
  background?: string;
  /** Paints the content box's own colour, whose dark version `derived.darkOwn` names, as `--re-own-content-bg`. */
  content?: string;
  /** The section's motion scope (sectionMotion in utils/motion.ts); its values that differ from the website's are in `style`. */
  motion?: Record<string, string>;
  /** The scrims a living section or content background needs (livingScrim), only where one does. */
  living?: { section?: LivingScrim; content?: LivingScrim };
  /** Indrammet (`ui.frame: 'framed'`, src/styles/look-page.css): the margin and corners the section sets itself. */
  frame?: Record<string, string>;
}

const PALETTE_REF = /^var\(--re-p-([a-z0-9-]{1,40})\)$/;
const FRAME_MARGIN = /^\d{1,2}(?:\.\d{1,2})?px$/;
const FRAME_MARGIN_MAX = 64;
const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** The colour the section's wrapper paints: LayeredSection a colour layer, SectionLayout its `bgColor` chain. */
function backgroundColour(section: Record<string, unknown>) {
  const ui = record(section.ui);
  if (purposeFamily(String(section.type))) {
    const background = resolveBackground(ui.background, { living: true });
    return background.kind === "colour" || background.kind === "living"
      ? background.color
      : undefined;
  }
  const specifics = record(section.specifics);
  return specifics.bgColor ?? ui.bgColor ?? specifics.farvebaggrund;
}

function opaqueHex(value: unknown) {
  const digits = typeof value === "string" ? HEX.exec(value)?.[1] : undefined;
  if (!digits) return undefined;
  const full = (
    digits.length <= 4
      ? [...digits].map((digit) => digit + digit).join("")
      : digits
  ).toLowerCase();
  if (full.length === 6) return `#${full}`;
  return full.endsWith("ff") ? `#${full.slice(0, 6)}` : undefined;
}

function ownColour(
  hex: string,
  derived: unknown,
  theme: unknown,
): SectionLookAttributes {
  const light = surfaceOf(record(derived).light);
  const dark = surfaceOf(record(derived).dark);
  if (!light || !dark) return {};
  return {
    "data-re-bg": hex,
    style: ownBackgroundStyle(hex, light, dark, theme),
    background: "var(--re-own-bg)",
  };
}

function colourAttributes(
  theme: { colorSystem: unknown },
  section: Record<string, unknown>,
): SectionLookAttributes {
  const colour = backgroundColour(section);
  if (typeof colour !== "string") return {};
  const ref = PALETTE_REF.exec(colour)?.[1];
  if (ref) {
    const key =
      resolvePaletteKey(theme.colorSystem, ref) ??
      rolePaletteKey(theme.colorSystem, ref);
    return key ? { "data-re-bg": key } : {};
  }
  const hex = opaqueHex(colour);
  return hex ? ownColour(hex, record(section.ui).lookDerived, theme) : {};
}

/** The content box's colour where it takes its dark version in dark (LayeredSection paints a colour layer only). */
export function ownContentColour(
  theme: unknown,
  section: unknown,
): string | undefined {
  const background = resolveBackground(
    record(record(record(section).ui).content).background,
    { living: true },
  );
  return background.kind === "colour" &&
    typeof background.color === "string" &&
    ownColourInDark(theme, background.color)
    ? ownColourCss(theme, background.color)
    : undefined;
}

function contentAttributes(
  theme: unknown,
  section: Record<string, unknown>,
  look: SectionLookAttributes,
): SectionLookAttributes {
  const colour = ownContentColour(theme, section);
  return colour
    ? {
        ...look,
        content: "var(--re-own-content-bg)",
        style: { ...look.style, "--re-own-content-bg": colour },
      }
    : look;
}

/** Both layers' text is the section's background's derived text, so both are checked against it. */
function livingScrims(
  theme: unknown,
  section: Record<string, unknown>,
  look: SectionLookAttributes,
): SectionLookAttributes["living"] {
  if (!purposeFamily(String(section.type))) return undefined;
  const ui = record(section.ui);
  const key = look["data-re-bg"];
  const own = key?.startsWith("#") ? ui.lookDerived : undefined;
  const scrimOf = (background: unknown) => {
    const living = resolveLivingBackground(background);
    const scrim =
      living && livingScrim(theme, living, own || !key ? "page" : key, own);
    return scrim && (scrim.light || scrim.dark) ? scrim : undefined;
  };
  const scrims = {
    section: scrimOf(ui.background),
    content: scrimOf(record(ui.content).background),
  };
  return scrims.section || scrims.content
    ? Object.fromEntries(Object.entries(scrims).filter(([, scrim]) => scrim))
    : undefined;
}

function frameOf(section: Record<string, unknown>) {
  const ui = record(section.ui);
  if (ui.frame !== "framed") return undefined;
  const frame: Record<string, string> = {};
  const { frameMargin, frameCorners } = ui;
  if (
    typeof frameMargin === "string" &&
    FRAME_MARGIN.test(frameMargin) &&
    parseFloat(frameMargin) <= FRAME_MARGIN_MAX
  )
    frame["--re-section-frame-margin"] = frameMargin;
  const corners =
    typeof frameCorners === "string" ? cornerLengths(frameCorners) : undefined;
  if (corners) frame["--re-section-frame-corners"] = corners;
  return frame;
}

/**
 * `{}` for a website that is not on the look system. A palette background names its key, after the palette's
 * redirects; an own colour needs the derived colours the CMS stored beside it in `ui.lookDerived`.
 */
export function sectionLookAttributes(
  theme: unknown,
  section: unknown,
): SectionLookAttributes {
  if (!isLookSystem(theme)) return {};
  const look = contentAttributes(
    theme,
    record(section),
    colourAttributes(theme, record(section)),
  );
  const living = livingScrims(theme, record(section), look);
  const frame = frameOf(record(section));
  return living || frame
    ? { ...look, ...(living ? { living } : {}), ...(frame ? { frame } : {}) }
    : look;
}
